import type { NaturalChestKind } from "./loot.ts";

/**
 * Pontos de interesse (doc 04, POIs: ordem e regras; Carimbos de terreno; doc 03, POIs de baús).
 * Regras por tipo (contagem, carimbo e distâncias) e o layout de cada um: âncoras com posição local
 * (metros, eixo +z para a "frente" do POI antes do giro), usadas pelo servidor (estruturas e
 * colisores) e pelo cliente (adereços desenhados a partir do plano). A seleção de âncoras usa o
 * stream do próprio POI.
 */
export type PoiType = "destrocos" | "boca" | "chegada" | "guardiao" | "gruta" | "acampamento" | "carranca" | "pedra" | "ruina" | "simpatia" | "nascimento";

export interface PoiRule {
  type: PoiType;
  name: string;
  /** Quantidade no Mutirão (mín., máx.). */
  count: readonly [number, number];
  /** Na Treta (sem destroços, Boca, chegada e guardiões; com pontos de nascimento). `undefined`: não existe. */
  treta?: readonly [number, number];
  /** Carimbo: raio plano e raio da transição (m). 0 = sem carimbo. */
  inner: number;
  outer: number;
  /** Pegada que recursos e outros POIs evitam (m). */
  footprint: number;
  /** Faltar abaixo do mínimo refaz a ilha (obrigatório). */
  required: boolean;
}

/** Ordem do planejador: do mais restrito ao mais flexível (doc 04). */
export const POI_RULES: readonly PoiRule[] = [
  { type: "destrocos", name: "Naufrágio", count: [1, 1], inner: 12, outer: 20, footprint: 22, required: true },
  { type: "boca", name: "Garganta do Dragão", count: [1, 1], inner: 0, outer: 0, footprint: 0, required: true },
  { type: "chegada", name: "Costa de chegada", count: [1, 1], inner: 10, outer: 18, footprint: 20, required: true },
  { type: "nascimento", name: "Ponto de nascimento", count: [0, 0], treta: [6, 6], inner: 6, outer: 12, footprint: 12, required: true },
  { type: "guardiao", name: "Santuário do Guardião", count: [5, 5], inner: 18, outer: 28, footprint: 30, required: true },
  { type: "gruta", name: "Caverna", count: [2, 4], treta: [2, 3], inner: 8, outer: 16, footprint: 18, required: false },
  { type: "acampamento", name: "Acampamento do Mercador", count: [2, 2], treta: [1, 2], inner: 22, outer: 34, footprint: 36, required: true },
  // Na Treta não há reviver, então nem Carranca (doc 02 §17).
  { type: "carranca", name: "Estátua da Fênix", count: [7, 9], inner: 6, outer: 10, footprint: 10, required: false },
  { type: "pedra", name: "Menir", count: [12, 16], treta: [8, 12], inner: 10, outer: 16, footprint: 16, required: false },
  { type: "ruina", name: "Ruína da Fortaleza", count: [3, 5], treta: [2, 4], inner: 15, outer: 24, footprint: 26, required: false },
  { type: "simpatia", name: "Baú de Bênção", count: [40, 60], treta: [30, 45], inner: 0, outer: 0, footprint: 3, required: false },
];

export const POI_RULE = new Map(POI_RULES.map((r) => [r.type, r]));

/** Cores dos Guardiões, na ordem de colocação, com o bioma preferido (doc 04, proposta). */
export const GUARDIAN_COLORS = [
  { color: "verde", biome: "mata" },
  { color: "azul", biome: "mangue", fallback: "praia" },
  { color: "vermelho", biome: "serra" },
  { color: "amarelo", biome: "cerrado" },
  { color: "rosa", biome: "praia" },
] as const;

/** Gema de cada Guardião, como o jogador a vê (doc 08); a cor continua sendo o id interno. */
export const GEM_NAMES: Record<(typeof GUARDIAN_COLORS)[number]["color"], string> = {
  verde: "Esmeralda",
  azul: "Safira",
  vermelho: "Rubi",
  amarelo: "Topázio",
  rosa: "Quartzo Rosa",
};

/**
 * Posição local de uma âncora: cartesiana (m) ou polar (ângulo `a` em rad a partir de +z, raio
 * `r`). O worldgen converte com a trigonometria determinística de `@eldrun/shared` (doc 07, A2).
 */
export type Anchor = { x: number; z: number } | { a: number; r: number };

/**
 * Colisor de um adereço: cilindro vertical, ou caixa nos eixos do POI (largura em x, profundidade em z;
 * o giro próprio do adereço não conta). Nos POIs girados de 90° em 90° a caixa fica alinhada aos eixos
 * do mundo; nos que giram livre, o worldgen a recorta em faixas alinhadas, todas dentro dela. `y`: base
 * acima do chão (caixa suspensa, como a laje da Caverna: passa-se por baixo e pisa-se em cima).
 */
export type PropCollider = { kind: "cilindro"; radius: number; height: number } | { kind: "caixa"; w: number; d: number; h: number; y?: number };

export type PoiProp = Anchor & {
  /** Nome do adereço; o visual vem das montagens (`POI_LOOKS`, F43), que repetem estes nos tipos sem peças novas. */
  mesh: string;
  /** Giro próprio (rad), somado ao do POI. */
  rot: number;
  collider?: PropCollider;
};

export interface PoiLayout {
  props: readonly PoiProp[];
  /** Baús naturais (tabela pelo tipo). */
  chests: readonly (Anchor & { kind: NaturalChestKind })[];
  /** Mascate do acampamento. */
  mascate?: Anchor;
  /** Veios de minério nas âncoras da gruta (tipos de recurso). */
  ores: readonly (Anchor & { type: "veio-ferro" | "veio-prata" })[];
  /** Estrutura interativa no centro (Carranca, Pedra de Encanto, altar do Guardião). */
  center?: string;
  /** O POI só gira de 90° em 90° (caixas alinhadas aos eixos). */
  rightAngles?: boolean;
}

const cyl = (radius: number, height: number): PropCollider => ({ kind: "cilindro", radius, height });
const box = (w: number, d: number, h: number, y?: number): PropCollider => ({ kind: "caixa", w, d, h, ...(y === undefined ? {} : { y }) });
const TAU = 6.283185307179586;
/** `n` âncoras polares num círculo de raio `r`, a partir do ângulo `offset`. */
const around = (n: number, r: number, offset = 0) => Array.from({ length: n }, (_, i) => ({ a: offset + (i / n) * TAU, r }));

const CAMP: PoiLayout = {
  props: [
    { mesh: "fogueira", x: 0, z: 0, rot: 0, collider: cyl(0.8, 0.6) },
    // Barracas em volta da fogueira, de frente para ela.
    ...around(3, 9, 0.4).map(({ a, r }): PoiProp => ({ mesh: "barraca", a, r, rot: a + Math.PI, collider: cyl(1.6, 2.4) })),
    { mesh: "carroca", x: -6.5, z: 5.5, rot: 0.6, collider: cyl(1.3, 1.6) },
    { mesh: "varal", x: 6.5, z: -6, rot: -0.4 },
    { mesh: "tronco-banco", x: 2.2, z: -1.4, rot: 1.1, collider: cyl(0.5, 0.5) },
  ],
  chests: around(3, 6, 1.45).map(({ a, r }) => ({ a, r, kind: "acampamento" as const })),
  mascate: { x: 2.8, z: 1.6 },
  ores: [],
};

const RUIN: PoiLayout = {
  // Paredes quebradas de um engenho (12 × 8 m) e a chaminé. Caixas: o POI gira só de 90° em 90°.
  props: [
    { mesh: "ruina-parede-longa", x: 0, z: -4, rot: 0, collider: box(10, 0.8, 2.6) },
    { mesh: "ruina-parede-curta", x: -6, z: -1, rot: 0, collider: box(0.8, 5, 1.8) },
    { mesh: "ruina-parede-curta", x: 6, z: 1.5, rot: 0, collider: box(0.8, 5, 1.4) },
    { mesh: "ruina-parede-meia", x: -3, z: 4, rot: 0, collider: box(5, 0.8, 1.1) },
    { mesh: "chamine", x: 4.5, z: -5.5, rot: 0, collider: cyl(1, 7) },
    { mesh: "moenda", x: 0, z: 0.5, rot: 0, collider: cyl(1.2, 1) },
  ],
  chests: [
    { x: -3.5, z: -2.5, kind: "engenho" },
    { x: 3, z: 2.5, kind: "engenho" },
    { x: -4, z: 2, kind: "engenho" },
  ],
  ores: [],
  rightAngles: true,
};

/**
 * Laje do teto da Caverna (`CAVE_SLAB` nas montagens, a mesma nas duas): caixas suspensas do fundo da
 * laje (4 m) até 4,8 m. A rocha tem o topo irregular (4,45–5,4 m): em cima, o pé fica no máximo 0,35 m
 * acima dela (nos patamares baixos) e até 0,6 m dentro dela (nas bossas). O modelo tem a borda recortada
 * (um vão na frente, no meio; nenhuma rocha sobre a frente do pilar da esquerda; lóbulos atrás): as caixas,
 * sobrepostas, seguem o contorno com o miolo (a 0,15 m da borda) sempre sob a rocha. O worldgen as junta
 * numa escada só de faixas alinhadas aos eixos (a Caverna gira livre).
 */
const CAVE_ROOF = { y: 4, h: 0.8 } as const;
const roof = (x0: number, x1: number, z0: number, z1: number): PoiProp => ({
  mesh: "gruta-teto",
  x: (x0 + x1) / 2,
  z: (z0 + z1) / 2,
  rot: 0,
  collider: box(x1 - x0, z1 - z0, CAVE_ROOF.h, CAVE_ROOF.y),
});
/** Caixas da laje (x0, x1, z0, z1 no espaço do POI), da frente para o fundo. */
const CAVE_ROOF_BOXES = [
  // A aba da frente, à direita do vão, e a ponta dela.
  roof(0.1, 3, -1.6, -0.2),
  roof(0.2, 2.6, -4.9, 0.4),
  roof(1.4, 2.2, -5.2, 0.8),
  // O bloco da frente à esquerda do vão.
  roof(-1.5, -0.6, -1.5, -0.5),
  // O meio, sobre a garganta, os pilares e o fundo.
  roof(-1, 0.4, -5.7, -0.9),
  roof(-3, 0.9, -5, -1.9),
  roof(-3.7, 3, -4.8, -2.4),
  // Atrás: a borda da esquerda, a faixa de ponta a ponta e o canto da direita.
  roof(-3.9, -3.4, -3.8, -3.1),
  roof(-3.6, 3.5, -4.9, -3.6),
  roof(1.9, 3.2, -5.5, -3.3),
  roof(1.1, 3.9, -5, -4.2),
];

const CAVE: PoiLayout = {
  // Boca de pedra voltada para o pátio plano (+z); a encosta fica atrás (−z).
  props: [
    { mesh: "gruta-pilar", x: -2.6, z: -2, rot: 0, collider: cyl(1.4, 4.5) },
    { mesh: "gruta-pilar", x: 2.6, z: -2, rot: 0.8, collider: cyl(1.4, 4.5) },
    ...CAVE_ROOF_BOXES,
    { mesh: "gruta-fundo", x: 0, z: -4.2, rot: 0, collider: cyl(2.6, 4) },
    { mesh: "pedregulho", x: -5, z: 1.5, rot: 0.3, collider: cyl(0.9, 1.2) },
    { mesh: "pedregulho", x: 4.8, z: 2.2, rot: 1.9, collider: cyl(0.9, 1.2) },
  ],
  // Na entrada, à frente dos pilares (baú e pilar não se encostam).
  chests: [
    { x: -1, z: 0.8, kind: "gruta" },
    { x: 1.1, z: 1.1, kind: "gruta" },
  ],
  ores: [
    { x: -4.5, z: -1, type: "veio-ferro" },
    { x: 4.5, z: -0.5, type: "veio-ferro" },
    { x: -3.5, z: 4, type: "veio-ferro" },
    { x: 3.5, z: 4.5, type: "veio-prata" },
  ],
};

const WRECK: PoiLayout = {
  props: [
    { mesh: "destrocos-casco", x: 0, z: 0, rot: 0.3, collider: cyl(2.2, 1.2) },
    { mesh: "destrocos-mastro", x: -3, z: 2.5, rot: 1.2, collider: cyl(0.3, 0.6) },
    { mesh: "destrocos-tabuas", x: 3.5, z: -2, rot: -0.7 },
    { mesh: "destrocos-tabuas", x: -2, z: -4, rot: 2.2 },
  ],
  chests: [{ x: 2.5, z: 1.8, kind: "destrocos" }],
  ores: [],
};

const SHRINE: PoiLayout = {
  // Arena de 18 m: anel de pedras e o altar colorido no centro (o Guardião é da F23).
  props: around(8, 12, 0.2).map(({ a, r }): PoiProp => ({ mesh: "santuario-pedra", a, r, rot: a, collider: cyl(0.8, 2.6) })),
  chests: [],
  ores: [],
  center: "santuario",
};

const STONE: PoiLayout = {
  props: around(3, 3.2, 0.5).map(({ a, r }): PoiProp => ({ mesh: "pedregulho", a, r, rot: a, collider: cyl(0.6, 0.8) })),
  chests: [],
  ores: [],
  center: "pedra-encanto",
};

const EMPTY: PoiLayout = { props: [], chests: [], ores: [] };

export const POI_LAYOUTS: Record<PoiType, PoiLayout> = {
  destrocos: WRECK,
  boca: EMPTY,
  chegada: { props: [{ mesh: "fogueira-apagada", x: 0, z: -3, rot: 0 }], chests: [], ores: [] },
  nascimento: EMPTY,
  guardiao: SHRINE,
  gruta: CAVE,
  acampamento: CAMP,
  carranca: { props: [], chests: [], ores: [], center: "carranca" },
  pedra: STONE,
  ruina: RUIN,
  simpatia: EMPTY,
};

/** Estruturas do mundo nascidas dos POIs (o nome sai daqui). */
export const POI_STRUCTURE_NAMES: Record<string, string> = {
  carranca: "Estátua da Fênix",
  "pedra-encanto": "Menir",
  "pedra-chefe": "Menir Sombrio",
  "santuario-verde": "Santuário da Esmeralda",
  "santuario-azul": "Santuário da Safira",
  "santuario-vermelho": "Santuário do Rubi",
  "santuario-amarelo": "Santuário do Topázio",
  "santuario-rosa": "Santuário do Quartzo",
};
